import React, { useState } from 'react';
import {
  GridColDef, GridRenderCellParams, GridRowsProp, GridValueGetterParams,
} from '@mui/x-data-grid';
import {
  Box, Button, CircularProgress, Typography,
} from '@mui/material';
import { IStudent } from '../interfaces';
import CardTableWrapper from '../../shared/custom/dataGrid/CardTableWrapper';
import CustomDataGrid from '../../shared/custom/dataGrid/CustomDataGrid';
import StudentsStatus from '../../shared/custom/status/StudentsStatus';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import TableMenu from '../../shared/custom/tableMenu.tsx/TableMenu';
import { addStudentForActions, setStudentsDeleteModalState, setStudentsModalState } from '../slice/studentSlice';
import InfoDialog from '../../shared/custom/dialog/InfoDialog';
import { deleteStudent } from '../actions/studentsActions';

interface IStudentsTableProps {
  students: IStudent[];
}

const StudentsTable = ({ students }: IStudentsTableProps ) => {
  const dispatch = useAppDispatch();
  const { loadingDelete, modalDelete, student } = useAppSelector(( state ) => state.student );

  const handleOpenEditModal = ( id: string ) => {
    dispatch( addStudentForActions( id ));
    dispatch( setStudentsModalState( true ));
  };

  const handleOpenDeleteModal = ( id: string ) => {
    dispatch( addStudentForActions( id ));
    dispatch( setStudentsDeleteModalState( true ));
  };

  const acceptDelete = ( ) => {
    dispatch( deleteStudent( student?.id || '' ));
  };

  const [perPage, setPerPage] = useState( 25 );
  const rows: GridRowsProp = students.map(
    ( b: IStudent ) => ({
      id: b.id,
      name: b.name,
      lastname: b.lastname,
      email: b.email,
      created: b.created,
      active: b.active,
    }),
  );

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'NOMBRES',
      headerClassName: 'theme__header',
      flex: 1,
    },
    {
      field: 'lastname',
      headerName: 'APELLIDOS',
      headerClassName: 'theme__header',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
    },
    {
      field: 'email',
      headerName: 'EMAIL',
      headerClassName: 'theme__header',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
    },
    {
      field: 'created',
      headerName: 'CREADO',
      headerClassName: 'theme__header',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      renderCell: ( params: GridRenderCellParams<number> ) => {
        const date = params.value;
        return (
          <Typography variant="caption">
            {new Date( date ).toLocaleDateString( 'es-ES' )}
          </Typography>
        );
      },
    },
    {
      field: 'active',
      headerName: 'ESTADO',
      headerClassName: 'theme__header',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      renderCell: ( params: GridRenderCellParams<number> ) => {
        const status = params.value;
        return ( <StudentsStatus status={status} /> );
      },
    },
    {
      field: 'actions',
      headerName: 'ACCIONES',
      width: 200,
      headerClassName: 'theme__header',
      align: 'center',
      headerAlign: 'center',
      renderCell: ( params: GridValueGetterParams ) => {
        const idStudent = params.id as string;
        return (
          <TableMenu
            id={idStudent}
            handleOpenEdit={handleOpenEditModal}
            handleOpenDelete={handleOpenDeleteModal}
          />
        );
      },
    },
  ];

  return (
    <>
      <CardTableWrapper>
        <CustomDataGrid
          noDataMessage="Sin Estudiantes"
          rows={rows}
          columns={columns}
          pageSize={perPage}
          onPageSizeChange={setPerPage}
          rowsPerPageOptions={[25, 50, 75, 100]}
        />
      </CardTableWrapper>
      <InfoDialog
        open={modalDelete}
        title="Eliminar Estudiante"
        contentText="Estás seguro que deseas eliminar a este Estudiante, esta acción es irreversible."
        closeFunction={() => dispatch( setStudentsDeleteModalState( !!loadingDelete ))}
      >
        <Box textAlign="center" pt={3}>
          <Button onClick={() => acceptDelete()} disabled={loadingDelete} endIcon={loadingDelete && ( <CircularProgress size={10} /> )}>
            ¡Sí, Eliminar!
          </Button>
        </Box>
      </InfoDialog>
    </>
  );
};

export default StudentsTable;
